/**
 * finance-core.js — Timbrica loan / savings / tax engine.
 *
 * Pure math, zero DOM. Loaded by /loan-calculator in the browser and by
 * tests/finance/*.mjs under node (same file, no build step).
 *
 * Why this exists instead of the usual inline blade JS:
 *   the schedule math is the product. It has to be unit-testable against
 *   published bank figures, otherwise "our calculator says X" is unfalsifiable.
 *
 * ── The two accrual models (the whole accuracy story) ─────────────────────
 *   'simple' — interest = balance × rate/12. Every free web calculator does
 *              this. It silently assumes all months are equal.
 *   'actual' — interest = balance × rate × days_in_period / days_in_year,
 *              split at the year boundary so 365/366 is honest. This is what
 *              a real loan agreement says (RU: «проценты начисляются на
 *              остаток задолженности за фактическое количество дней»), and it
 *              is why a bank's own schedule never matches a web calculator.
 *   Both are exposed. Disagreement between them is a feature, not a bug —
 *   it is the number the user is actually confused about.
 *
 * Money is carried as float and rounded only at the boundary (r2). Balances
 * are compared with an epsilon so a 0.004 residue never spawns a phantom
 * final period.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (root) { root.FinanceCore = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
'use strict';

var DAY = 86400000;
var EPS = 0.005;           // half a cent — below this a balance is "paid off"
var MAX_PERIODS = 1200;    // 100 years; a runaway-loop backstop, never reached in practice

/* ───────────────────────────── dates ───────────────────────────── */

function pd(v) {
    if (typeof v === 'number') return v;
    if (v instanceof Date) return Date.UTC(v.getFullYear(), v.getMonth(), v.getDate());
    var p = String(v).slice(0, 10).split('-');
    return Date.UTC(+p[0], +p[1] - 1, +p[2]);
}
function toISO(ts) {
    var d = new Date(ts), p = function (n) { return String(n).padStart(2, '0'); };
    return d.getUTCFullYear() + '-' + p(d.getUTCMonth() + 1) + '-' + p(d.getUTCDate());
}
function daysInYear(y) { return ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 366 : 365; }
function daysBetween(a, b) { return Math.round((b - a) / DAY); }

/**
 * addMonths with end-of-month clamping: 31 Jan + 1 month = 28/29 Feb, and the
 * anchor day is remembered so 31 Jan → 28 Feb → 31 Mar (not 28 Mar). Getting
 * this wrong shifts every later period by a day and quietly changes the total.
 */
function addMonths(startTs, n, anchorDay) {
    var d = new Date(startTs);
    var day = anchorDay || d.getUTCDate();
    var y = d.getUTCFullYear(), m = d.getUTCMonth() + n;
    var lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return Date.UTC(y, m, Math.min(day, lastDay));
}

function r2(x) { return Math.round((x + Number.EPSILON) * 100) / 100; }

/* ───────────────────────────── accrual ───────────────────────────── */

/**
 * Interest accrued on `balance` over [from, to) at `annualPct`.
 * 'actual' splits the period at 1 Jan so each slice uses its own 365/366.
 */
function accrue(balance, annualPct, from, to, dayCount) {
    if (balance <= 0 || annualPct <= 0 || to <= from) return 0;
    if (dayCount === 'simple') return balance * annualPct / 100 / 12;
    var total = 0, cur = from;
    while (cur < to) {
        var y = new Date(cur).getUTCFullYear();
        var yearEnd = Date.UTC(y + 1, 0, 1);
        var seg = Math.min(yearEnd, to);
        total += balance * (annualPct / 100) * daysBetween(cur, seg) / daysInYear(y);
        cur = seg;
    }
    return total;
}

/** Classic annuity payment. i = 0 degenerates to straight-line. */
function annuityPayment(principal, annualPct, months) {
    if (months <= 0) return principal;
    var i = annualPct / 100 / 12;
    if (i <= 0) return principal / months;
    return principal * i / (1 - Math.pow(1 + i, -months));
}

/** Inverse: what principal does a given payment support? Used by affordability. */
function principalFromPayment(payment, annualPct, months) {
    if (months <= 0) return 0;
    var i = annualPct / 100 / 12;
    if (i <= 0) return payment * months;
    return payment * (1 - Math.pow(1 + i, -months)) / i;
}

/**
 * The contractual annuity payment. ONE formula for both accrual models,
 * because that is what a loan agreement actually says:
 *
 *     ОСЗ × ПС / [1 − (1 + ПС)^−ПП],  ПС = 1/12 of the annual rate
 *
 * ── Why this is not "the ÷12 shortcut" (measured 2026-08-04) ──────────────
 * An earlier version SOLVED this payment by bisection for 'actual', so that
 * 240 equal payments would close the loan to the cent. It closes, and it is
 * wrong: a real agreement fixes the payment by the formula above AND accrues
 * interest on the actual day count, which do not reconcile exactly — the
 * difference lands in the final, corrective payment. Every RU schedule has
 * one; that is why the last row never matches the rest.
 *
 * Solving instead produced a THIRD number that no bank, no calculator and no
 * model quotes. Benchmarked over 2 344 realistic RU contracts, the solved
 * payment drifted from the contractual one by up to 1 689 ₽/month on consumer
 * loans (198 ₽ on mortgages) — visible on the headline figure the borrower
 * compares against the bank's offer — while UNDERSTATING the true cost,
 * because equal payments amortise faster than the contract's do.
 *
 * The residue is handled where the contract puts it: `buildSchedule` closes
 * the balance in period N. See tests/finance/finance-core.mjs §2.
 */
function solvePayment(balance, annualPct, months, fromTs, anchorDay, dayCount) {
    if (months <= 0 || balance <= 0) return balance;
    // Contracts state a kopeck figure, and the schedule is built from THAT, not
    // from an unrounded float: 240 × 0.0025 ₽ of drift is small but it is the
    // bank's number that has to reproduce, not ours.
    return r2(annuityPayment(balance, annualPct, months));
}

/**
 * Does the contractual payment even cover a long month's interest?
 *
 * The honest, a-priori test for "this is not a loan": at 22% over 30 years the
 * formula payment sits BELOW the interest a 31-day period accrues, so the debt
 * grows and the closing payment explodes. Checked before the schedule runs,
 * because after the fact it is indistinguishable from a legitimately capitalised
 * single long month. In the 2026-08-04 sweep this predicate isolated every
 * misbehaving cell: among contracts that pass it, zero blow-ups in 2 344 cases.
 */
function paymentCoversInterest(principal, annualPct, months, dayCount) {
    if (annualPct <= 0 || principal <= 0 || months <= 0) return true;
    // Under ÷12 every period charges balance × i, and the annuity payment is
    // balance × i / (1 − (1+i)^−n), i.e. ALWAYS strictly larger — there is
    // nothing to warn about. Measuring a 31-day month against a schedule that
    // has no days flagged 200%-for-30-years as "capitalising" while its own
    // capitalizedPeriods was 0.
    if (dayCount !== 'actual') return true;
    return annuityPayment(principal, annualPct, months) > principal * (annualPct / 100) * 31 / 365;
}

/**
 * Reverse solves. A borrower is very often quoted a PAYMENT and left to work
 * backwards ("банк одобрил 45 000 в месяц — это на сколько лет?", "платёж и
 * срок называют, а какая тут ставка?"). Both are bisections over the forward
 * model, so they stay exact under either accrual mode.
 */
function solveTerm(principal, annualPct, payment, fromTs, anchorDay, dayCount) {
    if (payment <= 0 || principal <= 0) return null;
    // "Never amortises" is not "smaller than the first period's interest": a
    // stub first period (Jan 1 → Feb 10) legitimately accrues more than one
    // instalment. The honest test is the payment a 100-year term would need —
    // essentially interest-only. Below that the balance never falls.
    if (payment <= solvePayment(principal, annualPct, 1200, fromTs, anchorDay, dayCount)) return null;
    var lo = 1, hi = 1200;
    for (var i = 0; i < 40; i++) {
        var mid = Math.floor((lo + hi) / 2);
        if (solvePayment(principal, annualPct, mid, fromTs, anchorDay, dayCount) > payment) lo = mid + 1;
        else hi = mid;
        if (lo >= hi) break;
    }
    return Math.min(1200, Math.max(1, hi));
}
function solveRate(principal, months, payment, fromTs, anchorDay, dayCount) {
    if (payment <= 0 || principal <= 0 || months <= 0) return null;
    if (payment * months <= principal) return payment * months < principal ? null : 0;
    var lo = 0, hi = 200;
    for (var i = 0; i < 200; i++) {
        var mid = (lo + hi) / 2;
        if (solvePayment(principal, mid, months, fromTs, anchorDay, dayCount) < payment) lo = mid; else hi = mid;
        if (hi - lo < 1e-7) break;
    }
    return r2((lo + hi) / 2 * 1000) / 1000;
}

/* ───────────────────────────── XIRR ───────────────────────────── */

/**
 * Effective annual rate of an irregular cash flow (act/365), i.e. what the
 * loan really costs once fees and insurance are in the stream. This is the
 * honest cousin of RU ПСК / EU APR.
 *
 * Newton first (fast, and near-exact for well-behaved loan flows), bisection
 * as the guaranteed fallback — Newton alone diverges on flows with several
 * sign changes (prepayment-heavy schedules), and a silently diverged APR is
 * worse than none.
 */
function xnpv(rate, flows, t0) {
    var s = 0;
    for (var i = 0; i < flows.length; i++) {
        var yrs = (flows[i].ts - t0) / DAY / 365;
        s += flows[i].amount / Math.pow(1 + rate, yrs);
    }
    return s;
}
function xirr(flows, guess) {
    if (!flows || flows.length < 2) return null;
    var pos = false, neg = false;
    for (var i = 0; i < flows.length; i++) {
        if (flows[i].amount > 0) pos = true; else if (flows[i].amount < 0) neg = true;
    }
    if (!pos || !neg) return null;                 // no sign change → no root
    var t0 = flows[0].ts, rate = (typeof guess === 'number') ? guess : 0.1;

    for (var k = 0; k < 60; k++) {
        var f = xnpv(rate, flows, t0);
        if (!isFinite(f)) break;
        if (Math.abs(f) < 1e-7) return rate;
        var h = 1e-6;
        var d = (xnpv(rate + h, flows, t0) - f) / h;
        if (!isFinite(d) || Math.abs(d) < 1e-12) break;
        var next = rate - f / d;
        if (!isFinite(next) || next <= -0.9999) break;
        if (Math.abs(next - rate) < 1e-10) return next;
        rate = next;
    }
    // bisection over a range wide enough for anything a consumer loan can be
    var lo = -0.9999, hi = 20, flo = xnpv(lo, flows, t0);
    if (!isFinite(flo)) return null;
    for (var j = 0; j < 300; j++) {
        var mid = (lo + hi) / 2, fm = xnpv(mid, flows, t0);
        if (!isFinite(fm)) return null;
        if (Math.abs(fm) < 1e-9) return mid;
        if ((flo < 0) === (fm < 0)) { lo = mid; flo = fm; } else { hi = mid; }
    }
    return (lo + hi) / 2;
}

/* ───────────────────── Bank of Russia key rate ─────────────────────
   Shared with /penalty-calculator (same source of truth, kept in sync).
   Source: cbr.ru/hd_base/keyrate. The server keeps the live table
   (finance:key-rate, CBR KeyRateXML) and the page prints it — see
   extendWithLive below; this literal is the floor and equals the repo copy
   resources/data/finance/ru-key-rate.json (KeyRateTableTest).
   Used here for the deposit-interest tax allowance, which the law defines as
   1 000 000 ₽ × the MAXIMUM key rate observed on the 1st of any month of the
   tax year — so it is derived, never hardcoded, and stays right on its own. */
var KEY_RATE = [
    { from: '2013-09-13', rate: 5.50 }, { from: '2014-03-03', rate: 7.00 }, { from: '2014-04-28', rate: 7.50 },
    { from: '2014-07-28', rate: 8.00 }, { from: '2014-11-05', rate: 9.50 }, { from: '2014-12-12', rate: 10.50 },
    { from: '2014-12-16', rate: 17.00 }, { from: '2015-02-02', rate: 15.00 }, { from: '2015-03-16', rate: 14.00 },
    { from: '2015-05-05', rate: 12.50 }, { from: '2015-06-16', rate: 11.50 }, { from: '2015-08-03', rate: 11.00 },
    { from: '2016-06-14', rate: 10.50 }, { from: '2016-09-19', rate: 10.00 }, { from: '2017-03-27', rate: 9.75 },
    { from: '2017-05-02', rate: 9.25 }, { from: '2017-06-19', rate: 9.00 }, { from: '2017-09-18', rate: 8.50 },
    { from: '2017-10-30', rate: 8.25 }, { from: '2017-12-18', rate: 7.75 }, { from: '2018-02-12', rate: 7.50 },
    { from: '2018-03-26', rate: 7.25 }, { from: '2018-09-17', rate: 7.50 }, { from: '2018-12-17', rate: 7.75 },
    { from: '2019-06-17', rate: 7.50 }, { from: '2019-07-29', rate: 7.25 }, { from: '2019-09-09', rate: 7.00 },
    { from: '2019-10-28', rate: 6.50 }, { from: '2019-12-16', rate: 6.25 }, { from: '2020-02-10', rate: 6.00 },
    { from: '2020-04-27', rate: 5.50 }, { from: '2020-06-22', rate: 4.50 }, { from: '2020-07-27', rate: 4.25 },
    { from: '2021-03-22', rate: 4.50 }, { from: '2021-04-26', rate: 5.00 }, { from: '2021-06-15', rate: 5.50 },
    { from: '2021-07-26', rate: 6.50 }, { from: '2021-09-13', rate: 6.75 }, { from: '2021-10-25', rate: 7.50 },
    { from: '2021-12-20', rate: 8.50 }, { from: '2022-02-14', rate: 9.50 }, { from: '2022-02-28', rate: 20.00 },
    { from: '2022-04-11', rate: 17.00 }, { from: '2022-05-04', rate: 14.00 }, { from: '2022-05-27', rate: 11.00 },
    { from: '2022-06-14', rate: 9.50 }, { from: '2022-07-25', rate: 8.00 }, { from: '2022-09-19', rate: 7.50 },
    { from: '2023-07-24', rate: 8.50 }, { from: '2023-08-15', rate: 12.00 }, { from: '2023-09-18', rate: 13.00 },
    { from: '2023-10-30', rate: 15.00 }, { from: '2023-12-18', rate: 16.00 }, { from: '2024-07-29', rate: 18.00 },
    { from: '2024-09-16', rate: 19.00 }, { from: '2024-10-28', rate: 21.00 }, { from: '2025-06-09', rate: 20.00 },
    { from: '2025-07-28', rate: 18.00 }, { from: '2025-09-15', rate: 17.00 }, { from: '2025-10-27', rate: 16.50 },
    { from: '2025-12-22', rate: 16.00 }, { from: '2026-02-16', rate: 15.50 }, { from: '2026-03-23', rate: 15.00 },
    { from: '2026-04-27', rate: 14.50 }, { from: '2026-06-22', rate: 14.25 }, { from: '2026-07-27', rate: 14.00 }
];
KEY_RATE = extendWithLive(KEY_RATE, typeof globalThis !== 'undefined' ? globalThis.__KEY_RATE_LIVE : null);
/* The table the page printed (partials/key-rate-live) — taken only when it
   EXTENDS the literal unchanged: a printed table that rewrote history is a
   bug somewhere, and the hand-checked literal stays. */
function extendWithLive(lit, live) {
    if (!Array.isArray(live) || live.length < lit.length) return lit;
    for (var i = 0; i < live.length; i++) {
        var s = live[i];
        if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s.from) || !(s.rate > 0 && s.rate < 100)) return lit;
        if (i > 0 && s.from <= live[i - 1].from) return lit;
        if (i < lit.length && (s.from !== lit[i].from || s.rate !== lit[i].rate)) return lit;
    }
    return live.map(function (s) { return { from: s.from, rate: +s.rate }; });
}
var KR = KEY_RATE.map(function (k) { return { ts: pd(k.from), rate: k.rate }; });

function keyRateOn(ts, override) {
    if (override && override.ts && ts >= override.ts) return override.rate;
    var r = 0;
    for (var i = 0; i < KR.length; i++) { if (ts >= KR[i].ts) r = KR[i].rate; else break; }
    return r;
}
/** Max key rate on the 1st of each month of `year` — the deposit-tax base. */
function maxKeyRateInYear(year, override) {
    var max = 0;
    for (var m = 0; m < 12; m++) {
        var ts = Date.UTC(year, m, 1);
        if (ts > Date.now() + 366 * DAY) break;
        max = Math.max(max, keyRateOn(ts, override));
    }
    return max;
}

/* ───────────────────────── amortization ───────────────────────── */

/**
 * @param o {
 *   principal, annualRate, months, startDate,
 *   type: 'annuity' | 'diff',
 *   dayCount: 'actual' | 'simple',
 *   paymentDay: number|null,
 *   fees: { originationPct, originationFixed, monthlyFixed, insuranceAnnualPct },
 *   prepayments: [{ date, amount, mode:'term'|'payment', every:0|1|3|6|12, until:ISO|null }],
 *   rateChanges: [{ from: ISO, rate }],
 *   roundUpTo: number|null   // pay a round sum every month, surplus → principal
 * }
 */
function buildSchedule(o) {
    var principal = +o.principal || 0;
    var months = Math.max(1, Math.round(+o.months || 0));
    var baseRate = +o.annualRate || 0;
    var start = pd(o.startDate || new Date());
    var anchorDay = +o.paymentDay || new Date(start).getUTCDate();
    var dayCount = o.dayCount === 'simple' ? 'simple' : 'actual';
    var type = o.type === 'diff' ? 'diff' : 'annuity';
    var fees = o.fees || {};
    var roundUpTo = +o.roundUpTo || 0;

    var rateChanges = (o.rateChanges || [])
        .map(function (c) { return { ts: pd(c.from), rate: +c.rate }; })
        .filter(function (c) { return isFinite(c.ts) && c.rate >= 0; })
        .sort(function (a, b) { return a.ts - b.ts; });
    function rateAt(ts) {
        var r = baseRate;
        for (var i = 0; i < rateChanges.length; i++) { if (ts >= rateChanges[i].ts) r = rateChanges[i].rate; else break; }
        return r;
    }

    // Expand recurring prepayments into concrete dated events.
    var prepays = [];
    (o.prepayments || []).forEach(function (p) {
        var amt = +p.amount || 0;
        if (amt <= 0) return;
        var mode = p.mode === 'payment' ? 'payment' : 'term';
        var every = Math.max(0, Math.round(+p.every || 0));
        // Default to the FIRST payment date, not the loan start: a prepayment
        // dated on the start date lands before any period exists and would be
        // silently dropped.
        var first = p.date ? pd(p.date) : addMonths(start, 1, anchorDay);
        var until = p.until ? pd(p.until) : addMonths(start, months, anchorDay);
        if (!every) { prepays.push({ ts: first, amount: amt, mode: mode }); return; }
        var guard = 0;
        for (var ts = first; ts <= until && guard < MAX_PERIODS; ts = addMonths(first, ++guard * every, new Date(first).getUTCDate())) {
            prepays.push({ ts: ts, amount: amt, mode: mode });
        }
    });
    prepays.sort(function (a, b) { return a.ts - b.ts; });

    var originationFee = (+fees.originationFixed || 0) + principal * (+fees.originationPct || 0) / 100;
    var monthlyFixed = +fees.monthlyFixed || 0;
    var insAnnualPct = +fees.insuranceAnnualPct || 0;

    var balance = principal;
    var payment = type === 'annuity' ? solvePayment(principal, baseRate, months, start, anchorDay, dayCount) : 0;
    var scheduledPayment = payment;
    var rows = [];
    var prevDate = start;
    var totalInterest = 0, totalPrincipal = 0, totalFees = originationFee, totalPrepaid = 0;
    var pi = 0;                 // prepayment cursor
    var lastRateSeen = baseRate;
    // REFUSING to draw a schedule is reserved for a debt that genuinely runs
    // away (the backstop at the end of the loop). An earlier draft of this
    // change used the a-priori paymentCoversInterest() test here and measured
    // 20 false refusals in 1 008 cells — including a plain 3 млн / 15% / 30 лет
    // mortgage, whose first 31-day period capitalises and whose 28-day periods
    // more than make up for it. That is the same trap the comment further down
    // already warned about; the predicate is a WARNING, not a verdict.
    var negativeAmortization = false;
    var capitalizedPeriods = 0; // months where the payment did not cover interest

    for (var k = 1; k <= MAX_PERIODS && balance > EPS; k++) {
        var date = addMonths(start, k, anchorDay);
        var rateNow = rateAt(prevDate);
        // A mid-period rate change re-prices the annuity from the next period on,
        // which is how variable-rate contracts actually behave.
        if (type === 'annuity' && rateNow !== lastRateSeen) {
            var left = Math.max(1, months - (k - 1));
            payment = solvePayment(balance, rateNow, left, prevDate, anchorDay, dayCount);
            lastRateSeen = rateNow;
        }

        var interest = accrue(balance, rateNow, prevDate, date, dayCount);
        var remaining = Math.max(1, months - (k - 1));
        var due, principalPart;

        if (type === 'diff') {
            principalPart = balance / remaining;
            due = principalPart + interest;
        } else {
            due = payment;
            principalPart = due - interest;
            // principalPart may legitimately go negative for a single long month:
            // under actual-days accrual a 31-day period at a high rate can exceed
            // the fixed payment while 28-day periods more than make up for it.
            // Flagging that as "negative amortization" (an earlier version did)
            // aborted perfectly valid schedules — the real failure is a loan that
            // never closes, which is detected below.
            if (principalPart < 0) capitalizedPeriods++;
        }

        // "round up the payment" strategy — surplus goes straight to principal
        var roundUpExtra = 0;
        if (roundUpTo > 0 && type === 'annuity') {
            var rounded = Math.ceil(due / roundUpTo) * roundUpTo;
            roundUpExtra = rounded - due;
            if (roundUpExtra > 0 && balance - principalPart > EPS) {
                principalPart += roundUpExtra;
                due = rounded;
            } else { roundUpExtra = 0; }
        }

        // The corrective final payment. A contract fixes the instalment by the
        // ÷12 formula and accrues on actual days; the two never reconcile to the
        // cent, so period N settles whatever is left — which is exactly why the
        // last row of a real bank schedule differs from every row above it.
        // Without the `k >= months` arm the loan runs PAST its contractual date
        // (36 of 144 cells in the 2026-07 sweep did) — that overrun was read as
        // a bug in the payment and "fixed" by solving it; the actual defect was
        // a missing closing rule.
        var isFinal = false;
        if (principalPart >= balance - EPS || k >= months) {
            principalPart = balance;
            due = balance + interest;
            isFinal = true;
        }

        var insurance = insAnnualPct > 0 ? balance * insAnnualPct / 100 / 12 : 0;
        var periodFees = monthlyFixed + insurance;

        balance = balance - principalPart;
        totalInterest += interest;
        totalPrincipal += principalPart;
        totalFees += periodFees;

        var row = {
            n: k, date: toISO(date), days: daysBetween(prevDate, date),
            rate: rateNow, payment: due, interest: interest, principal: principalPart,
            fees: periodFees, balance: Math.max(0, balance), prepay: 0, roundUp: roundUpExtra
        };

        // Prepayments dated in (prevDate, date] land after the scheduled payment.
        var repriced = false;
        while (pi < prepays.length && prepays[pi].ts <= date) {
            if (prepays[pi].ts > prevDate && balance > EPS) {
                var amt = Math.min(prepays[pi].amount, balance);
                balance -= amt;
                totalPrincipal += amt;
                totalPrepaid += amt;
                row.prepay += amt;
                if (prepays[pi].mode === 'payment') repriced = true;
            }
            pi++;
        }
        row.balance = Math.max(0, balance);
        if (repriced && type === 'annuity' && balance > EPS) {
            var left2 = Math.max(1, months - k);
            payment = solvePayment(balance, rateAt(date), left2, date, anchorDay, dayCount);
        }

        rows.push(row);
        prevDate = date;
        if (isFinal && balance <= EPS) break;

        // Runtime backstop only. The term can no longer overrun (period N closes
        // the balance), so the outcome test that used to live here is gone; the
        // "this is not a loan" verdict is now taken up front by
        // paymentCoversInterest(), which the sweep showed separates the cells
        // cleanly. This catches a balance genuinely running away, e.g. after a
        // mid-term rate change into the degenerate zone.
        if (balance > principal * 3) { negativeAmortization = true; break; }
    }

    // Cash flow for the effective rate: money received now, money paid later.
    var flows = [{ ts: start, amount: principal - originationFee }];
    rows.forEach(function (r) {
        flows.push({ ts: pd(r.date), amount: -(r.payment + r.fees + r.prepay) });
    });
    var eff = xirr(flows);

    var totalPaid = totalInterest + totalPrincipal + totalFees;
    return {
        ok: !negativeAmortization,
        error: negativeAmortization ? 'negative_amortization' : null,
        type: type, dayCount: dayCount,
        // For a differentiated loan there is no single payment — report the first,
        // which is the one the user is deciding against (it is the largest).
        payment: r2(type === 'diff' ? (rows.length ? rows[0].payment : 0) : scheduledPayment),
        firstPayment: rows.length ? r2(rows[0].payment) : 0,
        lastPayment: rows.length ? r2(rows[rows.length - 1].payment) : 0,
        months: rows.length,
        originalMonths: months,
        // How much the corrective final payment differs from every other one.
        // Non-zero on almost every actual-days schedule — it IS the difference
        // between the contract's two clauses, and the number a borrower can
        // check against the last line of the bank's own schedule.
        finalAdjustment: rows.length > 1
            ? r2(rows[rows.length - 1].payment - (type === 'diff' ? rows[rows.length - 2].payment : scheduledPayment))
            : 0,
        // Actual-days accrual can run just under the ÷12 assumption the payment
        // was priced on, and then the debt is gone a month early. Real, and
        // worth saying out loud rather than silently reporting a shorter term.
        closesEarly: Math.max(0, months - rows.length),
        // A-priori: the contractual payment does not cover a 31-day period's
        // interest, so the debt leans on the corrective final payment. Measured
        // over 1 008 cells this flagged 100 out of 100 schedules whose tail blew
        // up or that failed to run the full term — with no misses. It is a
        // warning the UI shows, never a refusal to compute (see above).
        mayCapitalize: type === 'annuity' && !paymentCoversInterest(principal, baseRate, months, dayCount),
        capitalizedPeriods: capitalizedPeriods,
        startDate: toISO(start),
        endDate: rows.length ? rows[rows.length - 1].date : toISO(start),
        rows: rows,
        totals: {
            interest: r2(totalInterest),
            principal: r2(totalPrincipal),
            fees: r2(totalFees),
            origination: r2(originationFee),
            prepaid: r2(totalPrepaid),
            paid: r2(totalPaid),
            overpayPct: principal > 0 ? r2((totalInterest + totalFees) / principal * 100) : 0
        },
        effectiveRate: eff === null ? null : r2(eff * 100)
    };
}

/* ───────────────────────── rate stress test ─────────────────────────
   A floating-rate loan is sold on today's rate and lived with on tomorrow's.
   The question that decides whether a household survives it is not "what is
   the overpayment" but "how much will the payment be after the reset, and can
   I still pay it". So we return the payment AFTER the change, not just totals. */
function rateStress(base, deltas, afterMonths) {
    var plain = buildSchedule(Object.assign({}, base, { rateChanges: [] }));
    if (!plain.ok) return { ok: false, base: plain, rows: [] };

    var start = pd(base.startDate || new Date());
    var anchorDay = +base.paymentDay || new Date(start).getUTCDate();
    var changeTs = addMonths(start, Math.max(1, Math.round(afterMonths)), anchorDay);
    var changeISO = toISO(changeTs);

    /**
     * Payment of the first period that STARTS on/after the reset. A row is
     * dated by its END, so matching on the row date returns the last period of
     * the OLD rate and the stress test reports a zero jump.
     */
    function paymentAfter(sched) {
        for (var i = 0; i < sched.rows.length; i++) {
            var periodStart = i === 0 ? start : pd(sched.rows[i - 1].date);
            if (periodStart >= changeTs) return sched.rows[i].payment;
        }
        return sched.rows.length ? sched.rows[sched.rows.length - 1].payment : 0;
    }
    var basePayment = paymentAfter(plain);

    var rows = (deltas || []).map(function (d) {
        var s = buildSchedule(Object.assign({}, base, {
            rateChanges: [{ from: changeISO, rate: (+base.annualRate || 0) + d }]
        }));
        if (!s.ok) return { delta: d, ok: false };
        var pay = paymentAfter(s);
        return {
            ok: true,
            delta: d,
            newRate: r2((+base.annualRate || 0) + d),
            payment: r2(pay),
            growth: r2(pay - basePayment),
            growthPct: basePayment > 0 ? r2((pay / basePayment - 1) * 100) : 0,
            interest: r2(s.totals.interest),
            extraInterest: r2(s.totals.interest - plain.totals.interest),
            months: s.months
        };
    });

    return {
        ok: true,
        base: plain,
        basePayment: r2(basePayment),
        changeDate: changeISO,
        rows: rows.filter(function (r) { return r.ok; })
    };
}

/* ─────────────────── prepayment: the comparison ───────────────────
   The question every user actually has is not "what if I overpay" but
   "term or payment?". So we return BOTH, computed from the same base, plus
   the base itself — a single object the UI can render as a verdict. */
function comparePrepayment(base, prepayments) {
    function withMode(mode) {
        var o = Object.assign({}, base, {
            prepayments: (prepayments || []).map(function (p) { return Object.assign({}, p, { mode: mode }); })
        });
        return buildSchedule(o);
    }
    var plain = buildSchedule(Object.assign({}, base, { prepayments: [] }));
    var term = withMode('term');
    var pay = withMode('payment');
    return {
        base: plain,
        term: term,
        payment: pay,
        savedByTerm: r2(plain.totals.interest - term.totals.interest),
        savedByPayment: r2(plain.totals.interest - pay.totals.interest),
        monthsSaved: plain.months - term.months,
        newPaymentAfter: pay.rows.length ? r2(pay.rows[pay.rows.length - 1].payment) : 0,
        better: (plain.totals.interest - term.totals.interest) >= (plain.totals.interest - pay.totals.interest) ? 'term' : 'payment'
    };
}

/* ───────────────────────── refinancing ─────────────────────────
   Three futures, not one:
     keep   — do nothing
     refi   — new rate, new term, pay the new (lower) payment
     refiKeep — new rate, but keep paying the OLD payment (the option every
                bank calculator omits, and usually the best one)                 */
function refinance(o) {
    var balance = +o.balance || 0;
    var months = Math.max(1, Math.round(+o.remainingMonths || 0));
    var oldRate = +o.currentRate || 0;
    var newRate = +o.newRate || 0;
    var newMonths = Math.max(1, Math.round(+o.newMonths || months));
    var costsFixed = +o.costsFixed || 0;
    var costsPct = +o.costsPct || 0;
    var start = o.startDate || toISO(Date.now());
    var dayCount = o.dayCount || 'actual';
    var costs = costsFixed + balance * costsPct / 100;
    var rollIn = !!o.rollCostsIn;

    var keep = buildSchedule({ principal: balance, annualRate: oldRate, months: months, startDate: start, dayCount: dayCount });
    var newPrincipal = rollIn ? balance + costs : balance;
    var refi = buildSchedule({ principal: newPrincipal, annualRate: newRate, months: newMonths, startDate: start, dayCount: dayCount });

    // same monthly outlay as before → term collapses
    var refiKeep = null;
    if (keep.payment > refi.payment + 0.01) {
        var extra = keep.payment - refi.payment;
        refiKeep = buildSchedule({
            principal: newPrincipal, annualRate: newRate, months: newMonths, startDate: start, dayCount: dayCount,
            prepayments: [{ date: addMonths(pd(start), 1, null), amount: extra, mode: 'term', every: 1 }]
        });
    }

    var monthlySaving = r2(keep.payment - refi.payment);
    var outOfPocket = rollIn ? 0 : costs;
    var breakEven = monthlySaving > 0 ? Math.ceil(outOfPocket / monthlySaving) : null;
    var netSaving = r2(keep.totals.paid - refi.totals.paid - outOfPocket);

    return {
        costs: r2(costs), keep: keep, refi: refi, refiKeep: refiKeep,
        monthlySaving: monthlySaving,
        breakEvenMonths: breakEven,
        netSaving: netSaving,
        netSavingKeepingPayment: refiKeep ? r2(keep.totals.paid - refiKeep.totals.paid - outOfPocket) : null,
        monthsSavedKeepingPayment: refiKeep ? (keep.months - refiKeep.months) : null,
        worthIt: netSaving > 0
    };
}

/* ───────────────────────── affordability ───────────────────────── */
/**
 * Two constraints live here and they are NOT the same rule:
 *
 *   bank   — ПДН (Указание ЦБ 4892-У): payments on ALL loans, the new one
 *            included, against income. Utilities, insurance and property tax
 *            are NOT part of it — no Russian bank counts your коммуналка.
 *   budget — the same allowance minus the running costs of owning the thing.
 *            Stricter, and the honest answer to "what can I actually carry",
 *            but it is ours, not the lender's.
 *
 * The headline stays the stricter of the two. What changed on 2026-08-04 is
 * that the function now returns WHICH constraint bit, and at what income it
 * stops biting — because `Math.max(0, …)` was silently turning "your existing
 * debts already exceed the allowance" into a bare `0`, and a bare 0 is not an
 * answer. A user with 2 030 000 ₽/mo of other payments raised his income 100×,
 * watched the tool keep saying 0 ₽, and wrote in to ask how that works
 * (production session 2f04c7ae, 2026-08-03). The maths was right; the reply
 * was missing.
 */
function affordability(o) {
    var income = +o.income || 0;
    var otherPayments = +o.otherPayments || 0;
    var dti = +o.maxDtiPct || 40;
    var rate = +o.annualRate || 0;
    var months = Math.max(1, Math.round(+o.months || 240));
    var down = +o.downPayment || 0;
    var extraMonthly = +o.extraMonthly || 0;      // property tax / insurance / utilities

    var budget = income * dti / 100;              // the whole debt-service allowance
    var committed = otherPayments + extraMonthly; // what is spoken for before the new loan
    var bankPayment = Math.max(0, budget - otherPayments);   // lender's view: ПДН only
    var maxPayment = Math.max(0, budget - committed);        // ours: also the running costs

    var maxLoan = principalFromPayment(maxPayment, rate, months);
    var maxPrice = maxLoan + down;

    // Which input drove the answer to zero — the debts on their own, or the
    // running costs on top of them? The two need different advice, so they are
    // different values, not one "it is zero" flag.
    var binding = null;
    if (maxPayment <= 0) binding = otherPayments >= budget ? 'debt' : 'costs';

    return {
        maxPayment: r2(maxPayment),
        maxLoan: r2(maxLoan),
        maxPrice: r2(maxPrice),
        dtiUsed: dti,
        downPaymentPct: maxPrice > 0 ? r2(down / maxPrice * 100) : 0,

        budget: r2(budget),
        committed: r2(committed),
        binding: binding,
        // What the lender alone would allow, ignoring the cost of ownership.
        // Worth showing when the running costs are what killed the answer:
        // the bank would still lend, the household just could not carry it.
        bankPayment: r2(bankPayment),
        bankLoan: r2(principalFromPayment(bankPayment, rate, months)),
        // Income at which the allowance finally covers the commitments. Below
        // it the answer is exactly zero however the rest is arranged.
        incomeForAny: dti > 0 ? r2(committed / (dti / 100)) : null,
        // ПДН as the regulator defines it: loan payments only, new one included.
        existingDtiPct: income > 0 ? r2(otherPayments / income * 100) : 0,
        resultingDtiPct: income > 0 ? r2((otherPayments + maxPayment) / income * 100) : 0,

        incomeNeededFor: function (loan) {
            var pay = annuityPayment(loan, rate, months);
            return r2((pay + otherPayments + extraMonthly) / (dti / 100));
        }
    };
}

/* ───────────────────────── deposit / savings ───────────────────────── */
/**
 * @param o { principal, monthlyAdd, annualRate, months, startDate,
 *            capitalization: 'none'|'monthly'|'quarterly'|'yearly',
 *            addTiming: 'start'|'end', dayCount, taxMode: 'none'|'ru',
 *            ndflRate, keyRateOverride }
 */
function deposit(o) {
    var principal = +o.principal || 0;
    var add = +o.monthlyAdd || 0;
    var rate = +o.annualRate || 0;
    var months = Math.max(1, Math.round(+o.months || 12));
    var start = pd(o.startDate || new Date());
    var cap = o.capitalization || 'monthly';
    var dayCount = o.dayCount === 'simple' ? 'simple' : 'actual';
    var atStart = o.addTiming !== 'end';
    var capEvery = cap === 'monthly' ? 1 : cap === 'quarterly' ? 3 : cap === 'yearly' ? 12 : 0;

    var balance = principal, uncapitalized = 0, contributed = principal;
    var rows = [], prev = start;
    var interestByYear = {};
    var flows = [{ ts: start, amount: -principal }];

    // Interest is taxable in the year it is PAID OUT / credited, not the year it
    // accrues. A December accrual credited on 1 January is next year's income —
    // getting this wrong shifted a whole month across the tax-year boundary and
    // understated the tax by 10% in testing.
    var pendingTaxable = 0;
    function credit(ts) {
        if (pendingTaxable <= 0) return;
        var yr = new Date(ts).getUTCFullYear();
        interestByYear[yr] = (interestByYear[yr] || 0) + pendingTaxable;
        pendingTaxable = 0;
    }

    for (var k = 1; k <= months; k++) {
        var date = addMonths(start, k, null);
        if (atStart && add > 0) { balance += add; contributed += add; flows.push({ ts: prev, amount: -add }); }
        var interest = accrue(balance, rate, prev, date, dayCount);
        pendingTaxable += interest;

        if (capEvery && k % capEvery === 0) { balance += interest + uncapitalized; uncapitalized = 0; credit(date); }
        else { uncapitalized += interest; }

        if (!atStart && add > 0) { balance += add; contributed += add; flows.push({ ts: date, amount: -add }); }
        rows.push({ n: k, date: toISO(date), interest: interest, balance: balance + uncapitalized, contributed: contributed });
        prev = date;
    }
    credit(prev);                                   // maturity payout
    var gross = balance + uncapitalized;
    var totalInterest = gross - contributed;

    // RU: interest above 1 000 000 × max key rate on the 1st of any month is taxed.
    var tax = 0, taxDetail = [];
    if (o.taxMode === 'ru') {
        var ndfl = +o.ndflRate || 13;
        Object.keys(interestByYear).forEach(function (yr) {
            var yInt = interestByYear[yr];
            var maxKr = maxKeyRateInYear(+yr, o.keyRateOverride);
            var allowance = 1000000 * maxKr / 100;
            var taxable = Math.max(0, yInt - allowance);
            var t = taxable * ndfl / 100;
            tax += t;
            taxDetail.push({ year: +yr, interest: r2(yInt), allowance: r2(allowance), taxable: r2(taxable), tax: r2(t), keyRate: maxKr });
        });
        taxDetail.sort(function (a, b) { return a.year - b.year; });
    }

    flows.push({ ts: prev, amount: gross - tax });
    var eff = xirr(flows);

    return {
        rows: rows,
        contributed: r2(contributed),
        gross: r2(gross),
        interest: r2(totalInterest),
        tax: r2(tax),
        taxDetail: taxDetail,
        net: r2(gross - tax),
        profit: r2(gross - tax - contributed),
        effectiveRate: eff === null ? null : r2(eff * 100)
    };
}

/** Monthly contribution needed to reach `target`. Bisection: exact and total. */
function savingsGoal(o) {
    var target = +o.target || 0;
    var lo = 0, hi = Math.max(target, 1);
    for (var i = 0; i < 200; i++) {
        var mid = (lo + hi) / 2;
        var res = deposit(Object.assign({}, o, { monthlyAdd: mid }));
        if (res.net < target) lo = mid; else hi = mid;
    }
    var need = (lo + hi) / 2;
    return { monthlyAdd: r2(need), result: deposit(Object.assign({}, o, { monthlyAdd: need })) };
}

/* ───────────────────────── inflation ─────────────────────────
   Annual CPI, official series. Verified 2026-08-03.
   RU: Rosstat (ИПЦ, % за год). US: BLS CPI-U annual average.
   2026 entries are year-to-date and flagged so the UI can say so. */
var CPI = {
    ru: {
        source: 'Rosstat', verified: '2026-08-03', partialFrom: 2026,
        y: {
            1991: 160.4, 1992: 2508.8, 1993: 839.9, 1994: 215.1, 1995: 131.3, 1996: 21.8, 1997: 11.0,
            1998: 84.4, 1999: 36.5, 2000: 20.2, 2001: 18.6, 2002: 15.1, 2003: 12.0, 2004: 11.7,
            2005: 10.9, 2006: 9.0, 2007: 11.87, 2008: 13.28, 2009: 8.8, 2010: 8.78, 2011: 6.1,
            2012: 6.58, 2013: 6.45, 2014: 11.36, 2015: 12.91, 2016: 5.38, 2017: 2.52, 2018: 4.27,
            2019: 3.05, 2020: 4.91, 2021: 8.39, 2022: 11.94, 2023: 7.42, 2024: 9.52, 2025: 5.59,
            2026: 4.19
        }
    },
    us: {
        source: 'BLS CPI-U', verified: '2026-08-03', partialFrom: 2026,
        y: {
            1990: 5.4, 1991: 4.2, 1992: 3.0, 1993: 3.0, 1994: 2.6, 1995: 2.8, 1996: 3.0, 1997: 2.3,
            1998: 1.6, 1999: 2.2, 2000: 3.4, 2001: 2.8, 2002: 1.6, 2003: 2.3, 2004: 2.7, 2005: 3.4,
            2006: 3.2, 2007: 2.8, 2008: 3.8, 2009: -0.4, 2010: 1.6, 2011: 3.2, 2012: 2.1, 2013: 1.5,
            2014: 1.6, 2015: 0.1, 2016: 1.3, 2017: 2.1, 2018: 2.4, 2019: 1.8, 2020: 1.2, 2021: 4.7,
            2022: 8.0, 2023: 4.1, 2024: 2.9, 2025: 2.6, 2026: 2.7
        }
    }
};

function inflationFactor(series, fromYear, toYear, customPct) {
    if (customPct !== null && customPct !== undefined && customPct !== '') {
        var n = toYear - fromYear;
        return Math.pow(1 + (+customPct) / 100, n);
    }
    var data = (CPI[series] || CPI.ru).y;
    var f = 1, a = Math.min(fromYear, toYear), b = Math.max(fromYear, toYear);
    for (var y = a; y < b; y++) {
        var p = data[y + 1];
        if (p === undefined) return null;               // outside the series → say so
        f *= (1 + p / 100);
    }
    return toYear >= fromYear ? f : 1 / f;
}

/* Currency reforms the price index knows nothing about. Rosstat's series is a
   chain of PRICE indices, but on 1998-01-01 the rouble itself changed unit
   (1 000 old = 1 new): an amount dated 1997 or earlier is in old roubles, 1998
   or later in new ones. A span across the reform must change the unit as well —
   without it 1 000 ₽ of 1991 came out as «65.5 million ₽» today instead of
   ≈ 65.5 thousand (found 2026-10-01). The price factor itself is NOT touched:
   prices really did rise ×65 515, only the money changed its name. A custom
   rate is the user's own number and carries no currency history. */
var REDENOMINATION = { ru: { year: 1998, ratio: 1000 } };

/** Multiplier that carries an amount's currency unit from fromYear to toYear. */
function unitShift(series, fromYear, toYear) {
    var r = REDENOMINATION[series];
    if (!r) return 1;
    var oldFrom = fromYear < r.year, oldTo = toYear < r.year;
    if (oldFrom && !oldTo) return 1 / r.ratio;
    if (!oldFrom && oldTo) return r.ratio;
    return 1;
}

function inflation(o) {
    var amount = +o.amount || 0;
    var from = Math.round(+o.fromYear), to = Math.round(+o.toYear);
    var series = o.series || 'ru';
    var f = inflationFactor(series, from, to, o.customPct);
    if (f === null) return { ok: false, error: 'out_of_range' };
    var years = Math.abs(to - from);
    var custom = o.customPct !== null && o.customPct !== undefined && o.customPct !== '';
    var unit = custom ? 1 : unitShift(series, from, to);
    return {
        ok: true,
        equivalent: r2(amount * f * unit),
        redenomination: unit === 1 ? null : REDENOMINATION[series],
        factor: r2(f * 10000) / 10000,
        totalPct: r2((f - 1) * 100),
        annualisedPct: years > 0 ? r2((Math.pow(f, 1 / years) - 1) * 100) : 0,
        lostPct: r2((1 - 1 / f) * 100),
        series: o.series || 'ru',
        meta: CPI[o.series || 'ru'] ? { source: CPI[o.series || 'ru'].source, verified: CPI[o.series || 'ru'].verified } : null
    };
}

/** Fisher: what a nominal return is worth after inflation. */
function realRate(nominalPct, inflationPct) {
    return r2(((1 + nominalPct / 100) / (1 + inflationPct / 100) - 1) * 100);
}

/**
 * Today's-money cost of a payment stream. The honest answer to "переплата ×2".
 * A 30-year loan repaid in inflating money costs far less than the nominal sum.
 */
function discountedCost(schedule, inflationPct) {
    if (!schedule || !schedule.rows || !schedule.rows.length) return null;
    var t0 = pd(schedule.startDate), sum = 0;
    schedule.rows.forEach(function (r) {
        var yrs = (pd(r.date) - t0) / DAY / 365;
        sum += (r.payment + r.fees + r.prepay) / Math.pow(1 + inflationPct / 100, yrs);
    });
    return {
        nominal: schedule.totals.paid,
        real: r2(sum),
        savedByInflation: r2(schedule.totals.paid - sum),
        realOverpayPct: r2((sum - schedule.totals.principal) / schedule.totals.principal * 100)
    };
}

/* ───────────────────── progressive income tax ─────────────────────
   A generic bracket engine + a registry that carries a `verified` date per
   country. Tax tables go stale silently; a calculator that cannot say WHEN it
   was last checked is the actual defect in this category. */
var TAX = {
    ru: {
        name: 'RU', currency: 'RUB', verified: '2026-08-03', period: 'year',
        note: 'ndfl_progressive',
        brackets: [
            { upTo: 2400000, rate: 13 },
            { upTo: 5000000, rate: 15 },
            { upTo: 20000000, rate: 18 },
            { upTo: 50000000, rate: 20 },
            { upTo: null, rate: 22 }
        ],
        standardDeduction: 0,
        employeeSocial: [],                       // RU: employer pays social, not employee
        employerSocial: [{ upTo: null, rate: 30 }]
    },
    us: {
        name: 'US', currency: 'USD', verified: '2026-08-03', period: 'year',
        note: 'us_federal_only',
        brackets: [
            { upTo: 12400, rate: 10 },
            { upTo: 50400, rate: 12 },
            { upTo: 105700, rate: 22 },
            { upTo: 201775, rate: 24 },
            { upTo: 256225, rate: 32 },
            { upTo: 640600, rate: 35 },
            { upTo: null, rate: 37 }
        ],
        standardDeduction: 16100,
        employeeSocial: [{ label: 'fica', rate: 7.65 }],
        employerSocial: [{ upTo: null, rate: 7.65 }]
    }
};

function progressiveTax(base, brackets) {
    var tax = 0, prev = 0, detail = [];
    for (var i = 0; i < brackets.length; i++) {
        var b = brackets[i];
        var top = b.upTo === null || b.upTo === undefined ? Infinity : b.upTo;
        var slice = Math.max(0, Math.min(base, top) - prev);
        if (slice > 0) {
            var t = slice * b.rate / 100;
            tax += t;
            detail.push({ from: prev, to: top === Infinity ? null : top, rate: b.rate, amount: r2(slice), tax: r2(t) });
        }
        prev = top;
        if (base <= top) break;
    }
    return { tax: tax, detail: detail };
}

/**
 * @param o { gross, period:'month'|'year', country:'ru'|'us'|'custom',
 *            brackets (custom), standardDeduction, deductions, children,
 *            includeEmployerCost }
 */
function salary(o) {
    var period = o.period === 'month' ? 'month' : 'year';
    var grossAnnual = (+o.gross || 0) * (period === 'month' ? 12 : 1);
    var cfg = o.country === 'custom'
        ? { name: 'custom', brackets: o.brackets || [{ upTo: null, rate: +o.flatRate || 0 }], standardDeduction: +o.standardDeduction || 0, employeeSocial: (+o.socialRate ? [{ label: 'social', rate: +o.socialRate }] : []), employerSocial: [], verified: null }
        : (TAX[o.country] || TAX.ru);

    var deductions = (+o.deductions || 0) + (cfg.standardDeduction || 0);
    // RU standard child deduction, applied while cumulative income ≤ 450 000
    if (o.country === 'ru' && +o.children > 0) {
        var perMonth = 0, kids = Math.round(+o.children);
        var table = [1400, 2800, 6000];             // 1st, 2nd, 3rd+ (2025+ amounts)
        for (var c = 1; c <= kids; c++) perMonth += table[Math.min(c, 3) - 1];
        var monthsEligible = Math.min(12, Math.floor(450000 / Math.max(1, grossAnnual / 12)));
        deductions += perMonth * monthsEligible;
    }
    var base = Math.max(0, grossAnnual - deductions);
    var pt = progressiveTax(base, cfg.brackets);

    var social = 0, socialDetail = [];
    (cfg.employeeSocial || []).forEach(function (s) {
        var cap = s.upTo === null || s.upTo === undefined ? Infinity : s.upTo;
        var amt = Math.min(grossAnnual, cap) * s.rate / 100;
        social += amt;
        socialDetail.push({ label: s.label || 'social', rate: s.rate, amount: r2(amt) });
    });

    var netAnnual = grossAnnual - pt.tax - social;
    var employerCost = grossAnnual;
    if (o.includeEmployerCost) {
        (cfg.employerSocial || []).forEach(function (s) {
            var cap = s.upTo === null || s.upTo === undefined ? Infinity : s.upTo;
            employerCost += Math.min(grossAnnual, cap) * s.rate / 100;
        });
    }

    return {
        grossAnnual: r2(grossAnnual), grossMonthly: r2(grossAnnual / 12),
        taxableBase: r2(base), deductions: r2(deductions),
        tax: r2(pt.tax), taxDetail: pt.detail,
        social: r2(social), socialDetail: socialDetail,
        netAnnual: r2(netAnnual), netMonthly: r2(netAnnual / 12),
        effectiveRate: grossAnnual > 0 ? r2((pt.tax + social) / grossAnnual * 100) : 0,
        marginalRate: (function () { var last = pt.detail[pt.detail.length - 1]; return last ? last.rate : 0; })(),
        employerCost: r2(employerCost),
        verified: cfg.verified || null,
        note: cfg.note || null
    };
}

/** Reverse: what gross yields a given net? Bisection — exact under any bracket set. */
function grossFromNet(o) {
    var target = (+o.net || 0) * (o.period === 'month' ? 12 : 1);
    var lo = 0, hi = Math.max(target * 4, 1000);
    for (var i = 0; i < 200; i++) {
        var mid = (lo + hi) / 2;
        var r = salary(Object.assign({}, o, { gross: mid, period: 'year' }));
        if (r.netAnnual < target) lo = mid; else hi = mid;
    }
    var gross = (lo + hi) / 2;
    return { gross: r2(o.period === 'month' ? gross / 12 : gross), detail: salary(Object.assign({}, o, { gross: gross, period: 'year' })) };
}

/* ───────────────── RU property tax deduction (имущественный вычет) ─────────────────
   Two independent pots: the property itself (base 2 000 000 ₽) and mortgage
   interest actually paid (base 3 000 000 ₽). Refunds are capped each year by
   the NDFL that year — which is why "you get 650 000 ₽" is usually a lie
   about timing: it arrives over years. We return the year-by-year schedule. */
function ruPropertyDeduction(o) {
    var cost = +o.propertyCost || 0;
    var interestPaid = +o.mortgageInterest || 0;
    var annualIncome = +o.annualIncome || 0;
    var startYear = Math.round(+o.startYear || new Date().getUTCFullYear());
    var spouses = o.spouses ? 2 : 1;
    var withMortgage = !!o.withMortgage;
    var rate = +o.ndflRate || 13;

    var baseLimit = 2000000 * spouses;
    var interestLimit = 3000000 * spouses;
    var baseDeduction = Math.min(cost, baseLimit);
    var interestDeduction = withMortgage ? Math.min(interestPaid, interestLimit) : 0;

    var refundBase = baseDeduction * rate / 100;
    var refundInterest = interestDeduction * rate / 100;
    var totalRefund = refundBase + refundInterest;

    // NDFL actually withheld per year (progressive), per person
    var ndflPerYear = salary({ gross: annualIncome, period: 'year', country: 'ru' }).tax * spouses;

    var schedule = [], left = totalRefund, y = startYear, guard = 0;
    while (left > 0.5 && guard++ < 40) {
        var got = Math.min(left, ndflPerYear);
        schedule.push({ year: y, refund: r2(got), remaining: r2(left - got) });
        left -= got;
        y++;
        if (ndflPerYear <= 0) break;
    }

    return {
        baseDeduction: r2(baseDeduction), interestDeduction: r2(interestDeduction),
        refundBase: r2(refundBase), refundInterest: r2(refundInterest),
        totalRefund: r2(totalRefund),
        limitHitBase: cost > baseLimit,
        limitHitInterest: withMortgage && interestPaid > interestLimit,
        ndflPerYear: r2(ndflPerYear),
        years: schedule.length,
        schedule: schedule,
        unreachable: ndflPerYear <= 0
    };
}

/**
 * The loan's effective rate once the tax refund is counted as money back.
 * Nobody shows this, and it is the number that decides "ипотека или накопить".
 */
function effectiveRateWithRefund(schedule, refundSchedule, principal, originationFee) {
    if (!schedule || !schedule.rows || !schedule.rows.length) return null;
    var flows = [{ ts: pd(schedule.startDate), amount: principal - (originationFee || 0) }];
    schedule.rows.forEach(function (r) { flows.push({ ts: pd(r.date), amount: -(r.payment + r.fees + r.prepay) }); });
    (refundSchedule || []).forEach(function (s) {
        flows.push({ ts: Date.UTC(s.year + 1, 3, 1), amount: s.refund });   // refunds arrive the following spring
    });
    flows.sort(function (a, b) { return a.ts - b.ts; });
    var v = xirr(flows);
    return v === null ? null : r2(v * 100);
}

return {
    // dates / helpers
    pd: pd, toISO: toISO, addMonths: addMonths, daysInYear: daysInYear, daysBetween: daysBetween, r2: r2,
    accrue: accrue, annuityPayment: annuityPayment, principalFromPayment: principalFromPayment,
    paymentCoversInterest: paymentCoversInterest,
    solvePayment: solvePayment, solveTerm: solveTerm, solveRate: solveRate,
    xirr: xirr, xnpv: xnpv,
    // key rate
    KEY_RATE: KEY_RATE, keyRateOn: keyRateOn, maxKeyRateInYear: maxKeyRateInYear,
    // modules
    buildSchedule: buildSchedule, comparePrepayment: comparePrepayment, refinance: refinance,
    rateStress: rateStress,
    affordability: affordability, deposit: deposit, savingsGoal: savingsGoal,
    CPI: CPI, inflationFactor: inflationFactor, inflation: inflation, realRate: realRate,
    REDENOMINATION: REDENOMINATION, unitShift: unitShift,
    discountedCost: discountedCost,
    TAX: TAX, progressiveTax: progressiveTax, salary: salary, grossFromNet: grossFromNet,
    ruPropertyDeduction: ruPropertyDeduction, effectiveRateWithRefund: effectiveRateWithRefund
};
});
